import { Injectable } from "@angular/core";
import {
    Country,
    Land,
    ListedCertificate,
    MentionType,
    Nationaliteit,
    Nationality,
    ProfessionalGroup,
    ProfessionalGroupType,
    ProfessionalGroupValidity,
} from "@zorro/apiclient";
import { BehaviorSubject, Observable, zip } from "rxjs";
import { filter, map } from "rxjs/operators";
import { ReferenceDataService } from "../services/reference-data.service";
import { Answer } from "./models/answer";
import { Question, QuestionType } from "./models/question";
import { Suggestion } from "./models/suggestion";

@Injectable({
    providedIn: "root",
})
export class AdvieswijzerService {
    private readonly countryCodeNL: string = "6030";

    private readonly questions = new Map<string, Question>();
    private readonly suggestions = new Map<string, Suggestion>();
    private readonly answers = new Map<string, Answer>();

    private readonly answerChange = new BehaviorSubject<Answer[]>(null);
    private readonly currentQuestionChange = new BehaviorSubject<{
        q: Question;
        a: Answer;
    }>(null);
    private readonly suggestionChange = new BehaviorSubject<Suggestion>(null);
    private currentQuestion: Question;

    private mentionTypes: MentionType[];
    private language: string;

    /**
     * Emit an observable when the current question changes. Object contains a Question (_q_) and an optional Answer (_a_).
     * When using the _goBackTo_, _previous_ or _next_-functions, the answer needs to be prefilled (if one is available).
     * A new question does not have an answer yet, so when using _init_ or _next_-functions (with a previously unanswered question),
     * the _a_-object will be null.
     * @return Observable that emits on a change in current question and (optionally) the corresponding answer.
     */
    get currentQuestionChanges(): Observable<{ q: Question; a: Answer }> {
        return this.currentQuestionChange
            .asObservable()
            .pipe(filter((cQ) => !!cQ));
    }

    get answerChanges(): Observable<Answer[]> {
        return this.answerChange.asObservable().pipe(filter((a) => !!a));
    }

    get suggestionChanges(): Observable<Suggestion> {
        return this.suggestionChange.asObservable();
    }

    constructor(private readonly referenceDataService: ReferenceDataService) {
        this.currentQuestionChanges.subscribe(
            (cQ) => (this.currentQuestion = cQ.q)
        );
    }

    init(lang: string): Observable<Question[]> {
        this.language = lang;
        return zip(
            this.referenceDataService.getProfessionalGroups(),
            this.referenceDataService.getCountries(
                new Date(Date.UTC(1970, 0, 1))
            ),
            this.referenceDataService.getMentionTypes(),
            this.referenceDataService.getNationalities(),
            this.referenceDataService.getProfessionalGroupValidities(),
            this.referenceDataService.getListedCertificates()
        ).pipe(
            map(
                ([
                    pGroups,
                    countries,
                    mentions,
                    nats,
                    pGroupValidities,
                    listedCertificates,
                ]) => {
                    this.mentionTypes = mentions;
                    pGroups = this.filterProfessionalGroups(
                        pGroups,
                        pGroupValidities
                    );
                    this.createQuestions(
                        pGroups,
                        countries,
                        nats,
                        pGroupValidities,
                        listedCertificates
                    );
                    this.createSuggestions();
                    const firstQuestion = this.questions.values().next()
                        .value as Question;

                    this.currentQuestionChange.next({
                        q: firstQuestion,
                        a: null,
                    });
                    return Array.from(this.questions.values());
                }
            )
        );
    }

    goBackTo(questionKey: string) {
        // hide all answers down to questionKey, incl questionKey (filter for 'visible' answers only)
        this.hideDownstreamAnswers(questionKey);

        // reset currentQuestion back to questionKey (with corresponding answer) & clear suggestion
        const question = this.questions.get(questionKey);
        const previousAnswer = this.answers.get(questionKey);

        this.currentQuestionChange.next({ q: question, a: previousAnswer });
        this.suggestionChange.next(null);
    }

    previous(): void {
        const visibleAnswers = this.getVisibleAnswers();
        const currentIndex = visibleAnswers.indexOf(this.currentQuestion.id);

        let previousQuestionKey: string = null;
        // if currentQuestion is already in answerArray, get previous answer based on that
        if (currentIndex > 0) {
            previousQuestionKey = visibleAnswers[currentIndex - 1];
        } else {
            // if not, get most recent answer (last in array)
            previousQuestionKey = visibleAnswers.slice(-1)[0];
        }

        this.goBackTo(previousQuestionKey);
    }

    next(answer: Answer): void {
        // This automatically overwrites previous answers for the same question
        this.setAnswer(answer);
        const nextId = this.currentQuestion.determineNext();

        if (nextId.startsWith("suggestion")) {
            this.suggestionChange.next(this.suggestions.get(nextId));
        } else {
            const nextQuestion = this.questions.get(nextId);

            if (nextQuestion.id === "whatDoYouWantToDo") {
                nextQuestion.options = this.createWhatDoYouWantToDoOptions();
            }

            let possibleAnswer: Answer = null;
            if (this.answers.has(nextId)) {
                possibleAnswer = this.answers.get(nextId);
            }
            this.currentQuestionChange.next({
                q: nextQuestion,
                a: possibleAnswer,
            });
        }
    }

    private hideDownstreamAnswers(questionKey: string) {
        const answerKeys = this.getVisibleAnswers();
        const questionKeyIndex = answerKeys.indexOf(questionKey);
        answerKeys.forEach((key) => {
            if (answerKeys.indexOf(key) >= questionKeyIndex) {
                this.answers.get(key).visible = false;
            }
        });
        this.answerChange.next(Array.from(this.answers.values()));
    }

    private getVisibleAnswers() {
        return Array.from(this.answers.entries())
            .filter((qa) => qa[1].visible)
            .map((fqa) => fqa[0]);
    }

    private setAnswer(answer: Answer) {
        this.answers.set(this.currentQuestion.id, answer);
        this.answerChange.next(Array.from(this.answers.values()));
    }

    private filterProfessionalGroups(
        groups: ProfessionalGroup[],
        validities: ProfessionalGroupValidity[]
    ): ProfessionalGroup[] {
        return groups.filter((p) => {
            return validities.some(
                (v) =>
                    v.professionalGroupCode === p.code &&
                    v.startDate <= this.today() &&
                    (v.endDate == null || v.endDate >= this.today())
            );
        });
    }

    /*  Q#   id                           value of answer
     *   1   professionalGroup            professionalGroupCode
     *   2   workNationally               true = NL, false = foreign
     *   3   alreadyRegistered            Yes, Never, Expired
     *   4   certificateCountry           countryCode
     *   5   certificateRecognizedByBig   EB, VV or No
     *   6   certificateRecent            true = < 5 years, false = > 5 years
     *   7   certificateRecognizedByEer   true = yes, false = no
     *   8   nationality                  nationalityCode
     *   9   rightOfResidence             true = yes, false = no
     *  10   whatDoYouWantToDo            - Reregister = herregistratie
     *                                    - Specialism = vermelding specialisme
     *                                    - Prescribe = vermelding voorschrijfbevoegdheid
     *                                    - ProofOfRegistration = bewijs van inschrijving
     *                                    - Other = iets anders                                      */
    private createQuestions(
        pGroups: ProfessionalGroup[],
        countries: Country[],
        nationalities: Nationality[],
        professionalGroupValidities: ProfessionalGroupValidity[],
        listedCertificates: ListedCertificate[]
    ): void {
        // Q1
        this.createQuestion(
            "professionalGroup",
            "professionalGroupText",
            8,
            QuestionType.DropDown,
            this.createProfessionalGroupOptions(pGroups),
            () => {
                return this.isArticle3(pGroups, professionalGroupValidities)
                    ? "workNationally"
                    : "certificateCountry";
            }
        );

        // Q2
        this.createQuestion(
            "workNationally",
            "workNationallyText",
            7,
            QuestionType.Radio,
            [
                { key: "workNationallyOptionTrue", value: true },
                { key: "workNationallyOptionFalse", value: false },
            ],
            () => {
                return "alreadyRegistered";
            }
        );

        // Q3
        this.createQuestion(
            "alreadyRegistered",
            "alreadyRegisteredText",
            6,
            QuestionType.Radio,
            [
                { key: "alreadyRegisteredOptionYes", value: "Yes" },
                { key: "alreadyRegisteredOptionNever", value: "Never" },
                { key: "alreadyRegisteredOptionExpired", value: "Expired" },
            ],
            () => {
                const answer = this.getStringAnswer("alreadyRegistered");
                if (this.getBooleanAnswer("workNationally")) {
                    switch (answer) {
                        case "Yes":
                            return "whatDoYouWantToDo";
                        case "Never":
                            return "certificateCountry";
                        default:
                            return "suggestionRegistrationAfterStrikedOut";
                    }
                }

                return answer === "Never"
                    ? "suggestionCertificateOfNonRegistration"
                    : "suggestionCcps";
            }
        );

        // Q4
        this.createQuestion(
            "certificateCountry",
            "certificateCountryText",
            5,
            QuestionType.DropDown,
            this.createCertificateCountryOptions(countries),
            () => {
                if (this.isArticle3(pGroups, professionalGroupValidities)) {
                    if (
                        this.isReregisterObligated(
                            pGroups,
                            professionalGroupValidities
                        ) &&
                        (this.isCertificateDutch() ||
                            this.isAutomaticRecognitionPossible(
                                countries,
                                listedCertificates
                            ))
                    ) {
                        return "certificateRecent";
                    }

                    if (
                        !this.isReregisterObligated(
                            pGroups,
                            professionalGroupValidities
                        ) &&
                        this.isCertificateDutch()
                    ) {
                        return "suggestionRegistrationDutchCertificateWeb";
                    }

                    if (
                        !this.isReregisterObligated(
                            pGroups,
                            professionalGroupValidities
                        ) &&
                        this.isAutomaticRecognitionPossible(
                            countries,
                            listedCertificates
                        )
                    ) {
                        return "suggestionRegistrationAutomaticCertificateWeb";
                    }

                    return "certificateRecognizedByBig";
                }

                return this.isCertificateDutch()
                    ? "suggestionVoluntarilyRegistration"
                    : "certificateRecognizedByBig";
            }
        );

        // Q5
        this.createQuestion(
            "certificateRecent",
            "certificateRecentText",
            1,
            QuestionType.Radio,
            [
                { key: "certificateRecentOptionYes", value: true },
                { key: "certificateRecentOptionNo", value: false },
            ],
            () => {
                const answer = this.getBooleanAnswer("certificateRecent");
                if (
                    this.isReregisterObligated(
                        pGroups,
                        professionalGroupValidities
                    ) &&
                    this.isCertificateDutch()
                ) {
                    return answer
                        ? "suggestionRegistrationDutchCertificateWeb"
                        : "suggestionRegistrationDutchCertReRegPaper";
                } else if (
                    this.isReregisterObligated(
                        pGroups,
                        professionalGroupValidities
                    ) &&
                    this.isAutomaticRecognitionPossible(
                        countries,
                        listedCertificates
                    )
                ) {
                    return answer
                        ? "suggestionRegistrationAutomaticCertificateWeb"
                        : "suggestionRegistrationAutoCertReRegPaper";
                }

                return answer
                    ? "suggestionRegistrationEbvvPaper"
                    : "suggestionRegistrationEBReRegistrationPaper";
            }
        );

        // Q6
        this.createQuestion(
            "certificateRecognizedByBig",
            "certificateRecognizedByBigText",
            4,
            QuestionType.Radio,
            [
                { key: "certificateRecognizedByBigOptionEB", value: "EB" },
                { key: "certificateRecognizedByBigOptionVV", value: "VV" },
                { key: "certificateRecognizedByBigOptionNo", value: "No" },
            ],
            () => {
                const answer = this.getStringAnswer(
                    "certificateRecognizedByBig"
                );
                const certIsEer = this.isCertificateEer(countries);
                if (this.isArticle3(pGroups, professionalGroupValidities)) {
                    if (
                        answer === "EB" &&
                        !this.isReregisterObligated(
                            pGroups,
                            professionalGroupValidities
                        )
                    ) {
                        return "suggestionRegistrationEbvvPaper";
                    } else if (answer === "EB") {
                        return "certificateRecent";
                    } else if (answer === "VV") {
                        return "suggestionRegistrationEbvvPaper";
                    } else if (answer === "No" && certIsEer) {
                        return "suggestionEBRegistration";
                    } else {
                        return "certificateRecognizedByEer";
                    }
                }

                if (answer === "EB" || answer === "VV") {
                    return "suggestionVoluntarilyRegistration";
                } else {
                    return certIsEer
                        ? "suggestionEB"
                        : "certificateRecognizedByEer";
                }
            }
        );

        // Q7
        this.createQuestion(
            "certificateRecognizedByEer",
            "certificateRecognizedByEerText",
            3,
            QuestionType.Radio,
            [
                { key: "certificateRecognizedByEerOptionYes", value: true },
                { key: "certificateRecognizedByEerOptionNo", value: false },
            ],
            () => {
                const answer = this.getBooleanAnswer(
                    "certificateRecognizedByEer"
                );
                if (this.isArticle3(pGroups, professionalGroupValidities)) {
                    return answer ? "nationality" : "suggestionVVRegistration";
                }

                return answer ? "nationality" : "suggestionVV";
            }
        );

        // Q8
        this.createQuestion(
            "nationality",
            "nationalityText",
            2,
            QuestionType.DropDown,
            this.createNationalityOptions(nationalities),
            () => {
                const isEerNationality = this.isEerNationality(nationalities);

                if (this.isArticle3(pGroups, professionalGroupValidities)) {
                    return isEerNationality
                        ? "suggestionEBRegistration"
                        : "rightOfResidence";
                }

                return isEerNationality ? "suggestionEB" : "rightOfResidence";
            }
        );

        // Q9
        this.createQuestion(
            "rightOfResidence",
            "rightOfResidenceText",
            1,
            QuestionType.Radio,
            [
                {
                    key: "rightOfResidenceOptionDutchPermit",
                    value: "DutchPermit",
                },
                { key: "rightOfResidenceOptionEUPermit", value: "EUPermit" },
                {
                    key: "rightOfResidenceOptionFamilyMember",
                    value: "FamilyMember",
                },
                {
                    key: "rightOfResidenceOptionNoRightOfResidence",
                    value: "NoRightOfResidence",
                },
            ],
            () => {
                const answer = this.getStringAnswer("rightOfResidence");
                if (answer !== "NoRightOfResidence") {
                    return this.isArticle3(pGroups, professionalGroupValidities)
                        ? "suggestionEBRegistration"
                        : "suggestionEB";
                }

                return this.isArticle3(pGroups, professionalGroupValidities)
                    ? "suggestionVVRegistration"
                    : "suggestionVV";
            }
        );

        // Q10
        this.createQuestion(
            "whatDoYouWantToDo",
            "whatDoYouWantToDoText",
            1,
            QuestionType.Radio,
            [],
            () => {
                switch (this.getStringAnswer("whatDoYouWantToDo")) {
                    case "Reregister":
                        return "suggestionReRegistration";
                    case "Specialism":
                        return "suggestionSpecialismMention";
                    case "Prescribe":
                        return "suggestionPrescribeMention";
                    case "ProofOfRegistration":
                        return "suggestionProofOfRegistration";
                    default:
                        return "suggestionUnfortunately";
                }
            }
        );
    }

    private createWhatDoYouWantToDoOptions(): { [key: string]: any }[] {
        const professionalGroupCode = this.getStringAnswer("professionalGroup");
        const options = [];
        options.push({
            key: "whatDoYouWantToDoOptionReregister",
            value: "Reregister",
        });
        options.push({
            key: "whatDoYouWantToDoOptionSpecialism",
            value: "Specialism",
        });

        if (
            this.mentionTypes.some(
                (s) => s.professionalGroupCode === professionalGroupCode
            )
        ) {
            options.push({
                key: "whatDoYouWantToDoOptionPrescribe",
                value: "Prescribe",
            });
        }

        options.push({
            key: "whatDoYouWantToDoOptionProofOfRegistration",
            value: "ProofOfRegistration",
        });
        options.push({ key: "whatDoYouWantToDoOptionOther", value: "Other" });

        return options;
    }

    private createQuestion(
        id: string,
        text: string,
        maxQuestionsLeft: number,
        type: QuestionType,
        options: { [key: string]: any }[],
        determineNext: () => string
    ): void {
        this.questions.set(id, {
            id,
            text,
            type,
            maxQuestionsLeft,
            options,
            determineNext,
        });
    }

    private createSuggestions(): void {
        // S1
        this.createSuggestion(
            "suggestionRegistrationDutchCertificateWeb",
            "BIGRegistratieMetNederlandsDiploma",
            false
        );

        // S2
        this.createSuggestion(
            "suggestionRegistrationAutomaticCertificateWeb",
            "BIGRegistratieMetAutomatischErkendDiploma",
            true
        );

        // S3
        this.createSuggestion(
            "suggestionRegistrationEbvvPaper",
            "BIGRegistratieMetEBOfVV",
            false
        );

        // S4
        this.createSuggestion(
            "suggestionRegistrationDutchCertReRegPaper",
            "BIGRegistratieMetNederlandsDiplomaEnEisenHerregistratie",
            false
        );

        // S5
        this.createSuggestion(
            "suggestionRegistrationAutoCertReRegPaper",
            "BIGRegistratieMetAutomatischErkendDiplomaEnEisenHerregistratie",
            true
        );

        // S6
        this.createSuggestion(
            "suggestionRegistrationEBReRegistrationPaper",
            "BIGRegistratieMetEBEnEisenHerregistratie",
            false
        );

        // S7
        this.createSuggestion(
            "suggestionEBRegistration",
            "ErkenningVanBeroepskwalificatiesDaarnaBIGRegistratie",
            true
        );

        // S8
        this.createSuggestion(
            "suggestionVVRegistration",
            "VerklaringVanVakbekwaamheidDaarnaBIGRegistratie",
            false
        );

        // S9
        this.createSuggestion(
            "suggestionRegistrationAfterStrikedOut",
            "BIGRegistratieNaDoorhaling",
            false
        );

        // S10
        this.createSuggestion(
            "suggestionReRegistration",
            "Herregistratie",
            false
        );

        // S11
        this.createSuggestion(
            "suggestionSpecialismMention",
            "VermeldingSpecialisme",
            false
        );

        // S12
        this.createSuggestion(
            "suggestionPrescribeMention",
            "VermeldingVoorschrijfbevoegdheid",
            false
        );

        // S13
        this.createSuggestion(
            "suggestionProofOfRegistration",
            "BewijsVanInschrijving",
            false
        );

        // S14
        this.createSuggestion("suggestionUnfortunately", "Helaas", false);

        // S15
        this.createSuggestion(
            "suggestionCcps",
            "CertificateOfCurrentProfessionalStatus",
            false
        );

        // S16
        this.createSuggestion(
            "suggestionCertificateOfNonRegistration",
            "CertificateOfNonRegistration",
            false
        );

        // S17
        this.createSuggestion(
            "suggestionVoluntarilyRegistration",
            "VrijwilligeRegistratieInBeroepsregister",
            false
        );

        // S18
        this.createSuggestion(
            "suggestionEB",
            "ErkenningVanBeroepskwalificaties",
            true
        );

        // S19
        this.createSuggestion(
            "suggestionVV",
            "VerklaringVanVakbekwaamheid",
            true
        );
    }

    private createSuggestion(
        id: string,
        piwikEvent: string,
        hasDisclaimer: boolean
    ): void {
        this.suggestions.set(id, {
            id,
            text: `${id}Text`,
            hasDisclaimer,
            logPiwikEvent: () => {
                const _paq = (window["_paq"] = window["_paq"] || []);
                _paq.push(["trackEvent", "ToonAdvies", "Advies", piwikEvent]);
            },
        });
    }

    private getStringAnswer(id: string): string {
        if (!this.answers.has(id)) {
            throw new Error("unknown answer: " + id);
        }

        return this.answers.get(id).answer.toString();
    }

    private getBooleanAnswer(id: string): boolean {
        if (!this.answers.has(id)) {
            throw new Error("unknown answer: " + id);
        }

        return this.answers.get(id).answer === true;
    }

    private createProfessionalGroupOptions(
        pGroups: ProfessionalGroup[]
    ): { [key: string]: any }[] {
        const professions = pGroups.map((pGroup) => {
            const descr =
                this.language === "nl" ? pGroup.title : pGroup.descriptionEn;
            return { key: descr, value: pGroup.code };
        });

        return this.sortByKey(professions, "key");
    }

    private createCertificateCountryOptions(
        countries: Country[]
    ): { [key: string]: any }[] {
        const sortedCountries = [...countries];
        const commonCountries = [
            Land.nederland,
            Land.belgie,
            Land.bondsrepubliekDuitsland,
        ];

        let options = sortedCountries.map((c) => {
            const name = this.language === "nl" ? c.name : c.nameEn;
            return { key: name, value: c.code };
        });
        options = this.sortByKey(options, "key");

        const reversedCommonCountries = [...commonCountries].reverse();
        reversedCommonCountries.forEach((c) => {
            const commonCountry = options.find((sc) => sc.value === c);
            if (commonCountry) {
                options.unshift(commonCountry);
            }
        });

        options.splice(3, 0, { key: "", value: null });

        return options;
    }

    private createNationalityOptions(
        nationalities: Nationality[]
    ): { [key: string]: any }[] {
        const sortedNationalities = [...nationalities];
        const commonNationalities = [
            Nationaliteit.nederlandse,
            Nationaliteit.belgische,
            Nationaliteit.burgerVandeBondsrepubliekDuitsland,
        ];

        let options = sortedNationalities.map((c) => {
            const name = this.language === "nl" ? c.name : c.nameEn;
            return { key: name, value: c.code };
        });
        options = this.sortByKey(options, "key");

        const reversedCommonNationalities = [...commonNationalities].reverse();
        reversedCommonNationalities.forEach((n) => {
            const commonNationality = options.find((sn) => sn.value === n);
            if (commonNationality) {
                options.unshift(commonNationality);
            }
        });

        options.splice(3, 0, { key: "", value: null });

        return options;
    }

    private isArticle3(
        professionalGroups: ProfessionalGroup[],
        professionalGroupValidities: ProfessionalGroupValidity[]
    ): boolean {
        const answer = this.getStringAnswer("professionalGroup");
        const professionalGroup = professionalGroups.find(
            (p) => p.code === answer
        );

        if (professionalGroup == null) {
            return null;
        }

        return professionalGroupValidities.some(
            (v) =>
                v.professionalGroupCode === professionalGroup.code &&
                (v.professionalGroupType.valueOf() ===
                    ProfessionalGroupType.artikel3.valueOf() ||
                    v.professionalGroupType.valueOf() ===
                        ProfessionalGroupType.artikel36A.valueOf()) &&
                v.applicationStartDate <= this.today() &&
                (v.endDate == null || v.endDate >= this.today())
        );
    }

    // Herregistratieplicht	Voor het beroep geldt:
    //       ( ProfessionalGroupTypeValidity.tpg_id = 1
    //          EN ProfessionalGroupTypeValidity.pgtv_Startdate < 1-1-2015 )
    // OF ( ProfessionalGroup.prgr_HerregistratieplichtVanaf ≠ leeg
    //          EN ProfessionalGroup.prgr_HerregistratieplichtVanaf =< vandaag )
    private isReregisterObligated(
        professionalGroups: ProfessionalGroup[],
        professionalGroupValidities: ProfessionalGroupValidity[]
    ): boolean {
        const answer = this.getStringAnswer("professionalGroup");
        const professionalGroup = professionalGroups.find(
            (p) => p.code === answer
        );

        if (professionalGroup == null) {
            return null;
        }

        return (
            professionalGroupValidities.some(
                (v) =>
                    v.professionalGroupCode === professionalGroup.code &&
                    this.article3AndStartDate(v)
            ) || this.reRegRestricted(professionalGroup)
        );
    }

    private article3AndStartDate(profGroupValidity: ProfessionalGroupValidity) {
        return (
            profGroupValidity.professionalGroupType.valueOf() ===
                ProfessionalGroupType.artikel3.valueOf() &&
            profGroupValidity.startDate < new Date(2015, 0, 1)
        );
    }

    private reRegRestricted(profGroup: ProfessionalGroup) {
        return (
            profGroup.reRegistrationFromDate !== null &&
            profGroup.reRegistrationFromDate <= this.today()
        );
    }

    // Automatische erkenning mogelijk:
    // Diplomaland ≠ Nederland
    // EN Diplomaland in EER
    // EN Het beroep komt voor in de stamtabel ListedCertificate
    //       (Europese lijst met aangewezen diploma’s)
    private isAutomaticRecognitionPossible(
        countries: Country[],
        listedCertificates: ListedCertificate[]
    ): boolean {
        const answer = this.getStringAnswer("professionalGroup");
        return (
            !this.isCertificateDutch() &&
            this.isCertificateEer(countries) &&
            listedCertificates.some((lc) => lc.professionalGroupCode === answer)
        );
    }

    // Voorschrijfbevoegdheid mogelijk	Het beroep komt voor in de stamtabel TypeMention

    private isCertificateDutch(): boolean {
        const answer = this.getStringAnswer("certificateCountry");
        return answer === this.countryCodeNL;
    }

    private isCertificateEer(countries: Country[]): boolean {
        const answer = this.getStringAnswer("certificateCountry");
        const country = countries.find((c) => c.code.toString() === answer);
        return country.isLidEer;
    }

    private isEerNationality(nationalities: Nationality[]): boolean {
        const answer = this.getStringAnswer("nationality");
        const nationality = nationalities.find(
            (n) => n.code.toString() === answer
        );
        return nationality && nationality.isEer;
    }

    private today(): Date {
        const now = new Date();
        return new Date(
            Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
        );
    }

    private sortByKey(array, key) {
        return array.sort((a, b) => {
            const x = a[key];
            const y = b[key];
            return x < y ? -1 : x > y ? 1 : 0;
        });
    }
}
